import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { Strip } from '@/components/Strip'
import { Alert as AlertCard } from '@/components/Alert'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type Alert = components['schemas']['Alert']

const SEVERITY_VARIANT: Record<string, 'danger' | 'warn' | 'info'> = { CRITICAL: 'danger', HIGH: 'danger', MEDIUM: 'warn', INFO: 'info' }

/**
 * `v-alerts` — process/09-SCREEN-SPECS/v-alerts.md. **No data table** — the panel is a strip of
 * condition cards, one `alert` component each, and appears only when `alertList` returns rows
 * (rule 1: a permanent fact is a report row, not this screen). A third snooze requires a reason
 * (rule 2) — enforced here by prompting for one from the third `snooze_count` onward, mirroring
 * `AlertSnooze`'s own server-side refusal rather than guessing at the threshold client-side.
 * `alerts.condition_key` never travels to this screen at all — `Alert` carries no such field.
 *
 * **A labelling finding, not silently copied**: the spec's own Actions table names the third
 * control "Reopen" against `alert.resolve`/`alertResolve`, but both the operation's and
 * `AlertState`'s own descriptions say reopening is what a RECURRING condition does automatically
 * server-side — there is no manual reopen call, and `alertResolve` only ever moves a condition
 * toward resolved. The control here is labelled "Resolve" and rendered only while a condition is
 * not already resolved, which is what the operation actually does.
 *
 * **`panel`/`grid-tools`/`grid-footer` added W168** — a real `panel` wrapping the whole surface, a
 * real "Refresh" `icon-button`, and a real `grid-footer` note stating rule 1 in the customer's own
 * words. **One of the spec's own two `strip` instances added** — the empty state, previously a
 * plain `Note`, is now the shared `Strip`. **The second `strip` (with its own `button`, per the
 * spec's "button in alert · strip") is not built** — no confidently-correct content was found for
 * it beyond the condition cards already rendered as `alert`s; reported rather than guessed at.
 *
 * **The "N open" count chip recoloured to `failure` (W172)** — the prototype's own alerts panel
 * header draws this exact chip as `solid-fail`, not `plain`. **`severity`/`state` were left
 * unchanged** — the prototype conveys those through the condition card's own border/background
 * colour (`Alert.tsx`'s `variant`, already correctly wired), never as a second chip; there is no
 * prototype-drawn chip for either field to match a colour against.
 *
 * **Rows are now grouped by `state`, not all rendered as one flat list of full cards.** `alertList`
 * takes no filter parameter at all — it serves every `AlertState` (`OPEN`/`ACKNOWLEDGED`/`SNOOZED`/
 * `RESOLVED`) in one response, matching the prototype's own header counts ("4 open · 1 snoozed ·
 * Acknowledged (14) · Resolved (203)"). The previous version rendered every returned row as a full
 * active `alert` card — including already-resolved conditions, forever, since nothing ever filtered
 * them out — and counted `rows.length` as "N open", which is only correct by accident while the
 * fixture happens to hold exclusively `OPEN` rows. Only `OPEN` rows get the full card with its
 * three actions now; `SNOOZED`/`ACKNOWLEDGED` rows render as a compact history line (statement plus
 * who/when, from the row's own `acknowledged_by`/`snooze_until` fields) with **no action button** —
 * neither `AlertSnooze` nor any other operation in `LLD-API-095`'s alert group exposes an
 * un-snooze or un-acknowledge call (snooze expiry is a server-side sweep per `AlertSnooze`'s own
 * doc comment), so the prototype's "Un-snooze"/"Reopen" buttons on those rows have no operation to
 * wire and are not built, for the same reason this file's own "Reopen" finding above already gives.
 * `RESOLVED` rows are counted only, never rendered as a row — matching "a permanent fact belongs in
 * a report, not on this screen."
 */
export function VAlerts() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const alerts = useQuery({
    queryKey: ['alerts', orgId, workspaceId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/alerts', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly Alert[]
    },
  })

  const acknowledge = useMutation({
    mutationFn: async (alertId: string) => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/alerts/{alert_id}/acknowledge', { params: { path: { org_id: orgId, workspace_id: workspaceId, alert_id: alertId } } })
      return unwrap(response as { data?: Alert; error?: Problem })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts', orgId, workspaceId] }),
  })

  const resolve = useMutation({
    mutationFn: async (alertId: string) => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/alerts/{alert_id}/resolve', { params: { path: { org_id: orgId, workspace_id: workspaceId, alert_id: alertId } } })
      return unwrap(response as { data?: Alert; error?: Problem })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts', orgId, workspaceId] }),
  })

  const snooze = useMutation({
    mutationFn: async ({ alertId, reason }: { alertId: string; reason?: string }) => {
      const client = getApiClient()
      const snoozeUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/alerts/{alert_id}/snooze', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, alert_id: alertId } },
        body: { snooze_until: snoozeUntil, ...(reason ? { reason } : {}) },
      })
      return unwrap(response as { data?: Alert; error?: Problem })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts', orgId, workspaceId] }),
  })

  function onSnooze(a: Alert) {
    const needsReason = (a.snooze_count ?? 0) >= 2
    const reason = needsReason ? (window.prompt('A reason is required from the third snooze onward:') ?? undefined) : undefined
    if (needsReason && !reason) return
    snooze.mutate({ alertId: a.alert_id, reason })
  }

  const problem = alerts.error instanceof ApiProblemError ? alerts.error.problem : undefined
  const rows = alerts.data ?? []
  const openRows = rows.filter((a) => a.state === 'OPEN')
  const snoozedRows = rows.filter((a) => a.state === 'SNOOZED')
  const acknowledgedRows = rows.filter((a) => a.state === 'ACKNOWLEDGED')
  const resolvedCount = rows.filter((a) => a.state === 'RESOLVED').length
  const historyRows = [...acknowledgedRows, ...snoozedRows]

  return (
    <div data-scr={unitId('v-alerts')}>
      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6">
        <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
          <h1 className="m-0 text-section font-semibold flex-1">Needs attention</h1>
          {openRows.length > 0 && <Chip variant="failure">{openRows.length} open</Chip>}
          {snoozedRows.length > 0 && <Chip variant="plain">{snoozedRows.length} snoozed</Chip>}
          {(acknowledgedRows.length > 0 || resolvedCount > 0) && (
            <span className="text-muted text-small">
              Acknowledged ({acknowledgedRows.length}) · Resolved ({resolvedCount})
            </span>
          )}
          <div data-cmp="grid-tools">
            <IconButton
              data-act={undefined}
              icon="i-refresh"
              label="Refresh"
              disabled={alerts.isFetching}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['alerts', orgId, workspaceId] })}
            />
          </div>
        </div>

        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

        {!problem && openRows.length === 0 && historyRows.length === 0 && !alerts.isLoading && <Strip>Nothing needs attention right now.</Strip>}

        <div className="flex flex-col gap-4">
          {openRows.map((a) => (
            <AlertCard key={a.alert_id} variant={SEVERITY_VARIANT[a.severity] ?? 'info'}>
              <div className="flex items-center gap-4 mb-2">
                <Chip variant="plain">{a.severity}</Chip>
                <Chip variant={a.state === 'RESOLVED' ? 'default' : 'plain'}>{a.state}</Chip>
                <span className="text-muted text-small">First seen {formatJobTime(a.first_seen_at)}</span>
                {a.recurrence_count > 1 && <span className="text-muted text-small">· recurred {a.recurrence_count}×</span>}
              </div>
              <div className="font-semibold mb-3">{a.statement}</div>
              {a.has_lost_commits && (
                <button
                  type="button"
                  className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-small mb-3 block"
                  onClick={() => navigate(`/lostcommits/${a.alert_id}`)}
                >
                  View the orphaned commits
                </button>
              )}
              <div className="flex gap-3">
                <Button data-act={actionId('alert.acknowledge')} ghost disabled={acknowledge.isPending} onClick={() => acknowledge.mutate(a.alert_id)}>
                  Acknowledge
                </Button>
                <Button data-act={actionId('alert.snooze')} ghost disabled={snooze.isPending} onClick={() => onSnooze(a)}>
                  Snooze
                </Button>
                <Button data-act={actionId('alert.resolve')} ghost disabled={resolve.isPending} onClick={() => resolve.mutate(a.alert_id)}>
                  Resolve
                </Button>
              </div>
            </AlertCard>
          ))}
        </div>

        {historyRows.length > 0 && (
          <div className="flex flex-col gap-2 mt-4">
            {historyRows.map((a) => (
              <div key={a.alert_id} className="flex items-center justify-between gap-4 px-4 py-3 bg-row rounded-1 text-small">
                <span>
                  {a.statement}
                  {a.state === 'ACKNOWLEDGED' && a.acknowledged_by?.display_name && (
                    <span className="text-muted"> · acknowledged by {a.acknowledged_by.display_name}{a.acknowledged_at ? `, ${formatJobTime(a.acknowledged_at)}` : ''}</span>
                  )}
                  {a.state === 'SNOOZED' && a.snooze_until && <span className="text-muted"> · snoozed until {formatJobTime(a.snooze_until)}</span>}
                </span>
                <Chip variant="plain">{a.state}</Chip>
              </div>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <div data-cmp="grid-footer" className="border-t border-bd mt-6 flex flex-col gap-3 pt-4">
            <Note>Every condition here is one that can clear — a permanent fact belongs in a report, not on this screen.</Note>
            <Note>
              <b>What rewrite detection can and cannot see.</b> We compare each backup against the one before it, so a
              rewrite made and reverted inside a single interval is invisible, and commits force-pushed away before
              their first backup were never ours. The actor is inferred from ref state — Bitbucket's reflog is not
              exposed — so where no real actor is available we say so rather than leaving a blank. Moving a{' '}
              <b>tag</b> is deliberate and common; alerting is scoped to branches.
            </Note>
            <Note>
              <b>Rules.</b> An alert is raised once per underlying condition, not once per job, so a recurring job
              failure updates a count rather than stacking rows. A recurring alert cannot be snoozed a third time
              without a recorded reason. Acknowledging never suppresses the condition on a report or in an export —
              only on this panel.
            </Note>
          </div>
        )}
      </div>
    </div>
  )
}
